import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Heart, Star, Users, Check, ExternalLink, ShieldCheck, CreditCard, Headphones } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

// Hosted PayPal Subscribe button. Set REACT_APP_PAYPAL_BUTTON_ID in /app/frontend/.env
// once you generate it in your PayPal business account (Merchant tools > PayPal buttons > Subscribe).
const PAYPAL_BUTTON_ID = process.env.REACT_APP_PAYPAL_BUTTON_ID || "";
const PAYPAL_BUSINESS_EMAIL = process.env.REACT_APP_PAYPAL_EMAIL || "mycnegocio@yahoo.com";

// Fallback: PayPal Donate link (single payment) if the hosted button is not yet configured.
const paypalUrl = () => {
  if (PAYPAL_BUTTON_ID) {
    return `https://www.paypal.com/cgi-bin/webscr?cmd=_s-xclick&hosted_button_id=${PAYPAL_BUTTON_ID}`;
  }
  const email = encodeURIComponent(PAYPAL_BUSINESS_EMAIL);
  return `https://www.paypal.com/donate?business=${email}&item_name=FaithCycle+Monthly+Support&amount=2.50&currency_code=USD`;
};

export default function Support() {
  const { lang, user, checkAuth } = useApp();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ supporters_count: 0 });
  const [showConfirm, setShowConfirm] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/support/stats");
        setStats(res.data);
      } catch {}
    })();
  }, []);

  const T = (en, es) => (lang === "es" ? es : en);

  const openPaypal = () => {
    window.open(paypalUrl(), "_blank", "noopener");
    setShowConfirm(true);
  };

  const startStripe = async () => {
    setStripeLoading(true);
    try {
      const res = await api.post("/payments/checkout", {
        lookup_key: "supporter_monthly",
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch {
      toast.error(T("Could not start Stripe checkout. Try again.", "No se pudo iniciar el pago con Stripe. Intenta de nuevo."));
      setStripeLoading(false);
    }
  };

  const claim = async () => {
    setClaiming(true);
    try {
      await api.post("/support/claim", { tier: "monthly_2_50", provider: "paypal" });
      await checkAuth();
      toast.success(T("Thank you for supporting FaithCycle 🌟", "¡Gracias por apoyar FaithCycle 🌟"));
      navigate("/home");
    } catch {
      toast.error(T("Could not activate. Try again.", "No se pudo activar. Intenta de nuevo."));
    } finally {
      setClaiming(false);
    }
  };

  const perks = [
    {
      Icon: Headphones,
      title: T("Audio Bible", "Biblia en audio"),
      desc: T(
        "Listen to any chapter with a natural narrator voice. Perfect for driving, walking or resting.",
        "Escucha cualquier capítulo con una voz de narrador natural. Ideal para conducir, caminar o descansar."
      ),
    },
    {
      Icon: Star,
      title: T("Supporter badge", "Insignia de Sostenedor"),
      desc: T(
        "A star ⭐ next to your name on every reflection, comment and profile.",
        "Una estrella ⭐ junto a tu nombre en cada reflexión, comentario y perfil."
      ),
    },
    {
      Icon: Heart,
      title: T("Keep FaithCycle alive", "Mantén FaithCycle viva"),
      desc: T(
        "Servers and community moderation are paid by supporters like you.",
        "Los servidores y la moderación de la comunidad los sostienen personas como tú."
      ),
    },
  ];

  return (
    <div>
      <div className="flex items-center gap-2 text-[var(--accent)]">
        <Heart className="w-4 h-4" strokeWidth={1.5} />
        <span className="uppercase tracking-[0.3em] text-[10px]">
          {T("Become a supporter", "Hazte Sostenedor")}
        </span>
      </div>
      <h1 className="font-serif text-4xl md:text-5xl tracking-tight mt-2">
        {T("Keep the light on.", "Mantén la luz encendida.")}
      </h1>
      <p className="mt-4 text-[var(--text-secondary)] text-lg max-w-2xl leading-relaxed">
        {T(
          "FaithCycle is built and maintained by one person. A small monthly donation covers servers and helps this app grow — free, calm and ad-free for everyone.",
          "FaithCycle la sostiene una sola persona. Una donación mensual pequeña cubre los servidores y ayuda a que la app siga creciendo — libre, serena y sin anuncios para todos."
        )}
      </p>

      {user?.is_supporter && (
        <div
          data-testid="already-supporter-banner"
          className="mt-8 rounded-2xl border border-[var(--accent)]/40 bg-[var(--highlight)]/40 p-5 flex items-center gap-3"
        >
          <Star className="w-5 h-5 text-[var(--accent)] fill-[var(--accent)]" strokeWidth={1.5} />
          <div>
            <p className="font-serif text-lg tracking-tight">
              {T("You are a supporter", "Ya eres Sostenedor")}
            </p>
            <p className="text-sm text-[var(--text-secondary)]">
              {T(
                "Thank you for keeping FaithCycle alive.",
                "Gracias por mantener a FaithCycle viva."
              )}
            </p>
          </div>
        </div>
      )}

      <div className="mt-10 max-w-lg rounded-3xl border border-[var(--border)] bg-[var(--surface-elevated)] p-8">
        <div className="flex items-center justify-between">
          <p className="uppercase tracking-[0.3em] text-[10px] text-[var(--text-secondary)]">
            {T("Monthly", "Mensual")}
          </p>
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-[var(--accent)]">
            <Users className="w-3 h-3" strokeWidth={1.5} />
            <span data-testid="supporters-count">{stats.supporters_count}</span>
            <span>{T("supporters", "sostenedores")}</span>
          </div>
        </div>
        <div className="mt-4 flex items-baseline gap-1">
          <span className="font-serif text-6xl tracking-tight">$2.50</span>
          <span className="text-[var(--text-secondary)] text-sm">
            / {T("month", "mes")}
          </span>
        </div>

        <ul className="mt-8 space-y-4">
          {perks.map(({ Icon, title, desc }, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="mt-1 w-8 h-8 rounded-full bg-[var(--highlight)]/50 flex items-center justify-center flex-shrink-0">
                <Icon className="w-4 h-4 text-[var(--accent)]" strokeWidth={1.5} />
              </span>
              <div>
                <p className="text-[var(--text-primary)]">{title}</p>
                <p className="text-sm text-[var(--text-secondary)] mt-0.5">{desc}</p>
              </div>
            </li>
          ))}
        </ul>

        {!user?.is_supporter && !showConfirm && (
          <div className="mt-8 space-y-3">
            <Button
              data-testid="stripe-subscribe-btn"
              onClick={startStripe}
              disabled={stripeLoading}
              className="w-full h-12 rounded-full bg-[var(--text-primary)] text-[var(--bg)] hover:bg-[var(--accent)] hover:text-white transition-colors duration-300 text-base font-normal"
            >
              {stripeLoading ? (
                <span className="opacity-70">{T("Opening checkout…", "Abriendo pago…")}</span>
              ) : (
                <>
                  <CreditCard className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  {T("Pay with card (Stripe)", "Pagar con tarjeta (Stripe)")}
                </>
              )}
            </Button>
            <div className="flex items-center gap-3 py-1">
              <div className="flex-1 h-px bg-[var(--border)]"></div>
              <span className="text-[10px] uppercase tracking-widest text-[var(--text-secondary)]">
                {T("or", "o")}
              </span>
              <div className="flex-1 h-px bg-[var(--border)]"></div>
            </div>
            <Button
              data-testid="paypal-subscribe-btn"
              onClick={openPaypal}
              className="w-full h-12 rounded-full bg-[#0070ba] hover:bg-[#005a9c] text-white text-base font-normal"
            >
              <PayPalIcon />
              {PAYPAL_BUTTON_ID
                ? T("Subscribe with PayPal", "Suscribirse con PayPal")
                : T("Donate with PayPal", "Donar con PayPal")}
              <ExternalLink className="w-3.5 h-3.5 ml-2" strokeWidth={1.5} />
            </Button>
          </div>
        )}

        {!user?.is_supporter && showConfirm && (
          <div className="mt-8 space-y-4" data-testid="confirm-block">
            <div className="rounded-2xl border border-[var(--border)] bg-[var(--surface)] p-4">
              <p className="text-sm text-[var(--text-primary)]">
                {T(
                  "Once your PayPal payment is complete, tap the button below to unlock your supporter perks.",
                  "Una vez completado el pago en PayPal, toca el botón para activar tus beneficios."
                )}
              </p>
            </div>
            <Button
              data-testid="claim-supporter-btn"
              onClick={claim}
              disabled={claiming}
              className="w-full h-12 rounded-full bg-[var(--accent)] hover:bg-[var(--accent)]/90 text-white text-base font-normal"
            >
              <Check className="w-4 h-4 mr-2" strokeWidth={1.5} />
              {T("I've paid, activate my perks", "Ya pagué, activar mis beneficios")}
            </Button>
            <button
              onClick={openPaypal}
              className="w-full text-xs text-[var(--text-secondary)] hover:text-[var(--accent)] transition-colors duration-200 uppercase tracking-widest"
            >
              {T("Open PayPal again", "Abrir PayPal de nuevo")}
            </button>
          </div>
        )}

        <div className="mt-6 flex items-center justify-center gap-2 text-[10px] uppercase tracking-widest text-[var(--text-secondary)]">
          <ShieldCheck className="w-3 h-3" strokeWidth={1.5} />
          <span>{T("Secure payment via Stripe or PayPal", "Pago seguro vía Stripe o PayPal")}</span>
        </div>
      </div>

      <p className="mt-10 max-w-2xl text-xs text-[var(--text-secondary)] leading-relaxed">
        {T(
          "You can cancel your PayPal subscription anytime from your PayPal account. All payments are processed by PayPal — we never see your card details.",
          "Puedes cancelar tu suscripción PayPal en cualquier momento desde tu cuenta de PayPal. Todos los pagos los procesa PayPal — nunca vemos los datos de tu tarjeta."
        )}
      </p>
    </div>
  );
}

const PayPalIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4 mr-2" aria-hidden="true">
    <path fill="#fff" d="M20.2 8.3c.1-.6.1-1.2 0-1.8-.5-2.4-2.7-3.5-5.4-3.5H9c-.5 0-.9.4-1 .9L5.9 18.4c0 .3.2.7.6.7h4l.7-4.2v.2c.1-.5.5-.9 1-.9h1.9c3.6 0 6.4-1.5 7.2-5.7Z"/>
  </svg>
);
